import Median from 'median-js-bridge';
/**
 * Détection unifiée de plateforme - Median uniquement
 * 
 * Note: Le type Window.median est déjà défini dans medianNativeBridge.ts
 */

export const isAndroidApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  return navigator.userAgent.includes('KokenAndroid') || !!(window as any).isGoNative;
};

export const isMobileApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isAndroidApp() || !!((window as any).webkit && (window as any).webkit.messageHandlers);
};

/**
 * Coque iOS NATIVE maison : l'application Swift du dépôt koken-ios-build
 * (depuis le 03/10/2026), qui remplace la coque Median.
 *
 * Elle se reconnaît à SON gestionnaire de messages `kokenCore`, que la coque
 * Median n'a pas ; la coque Median, elle, expose `JSBridge`, que la coque
 * maison n'a pas. On teste un gestionnaire NOMMÉ et pas la seule présence de
 * `webkit.messageHandlers`, que d'autres vues web exposent aussi.
 *
 * À quoi ça sert : cette coque recrée `window.median` pour quelques appels
 * (retour haptique, avis, vidage du cache) mais pas avec le même effet que
 * Median. Les appels qui diffèrent se gardent ici, un par un, plutôt que de
 * déclarer la coque « Median » en bloc : cela réveillerait d'un coup des
 * chemins jamais essayés sur cette coque (voir medianNativeBridge.isMedianNative).
 */
export const isKokenIosShell = (): boolean => {
  if (typeof window === 'undefined') return false;
  const handlers = (window as any).webkit?.messageHandlers;
  return !!handlers?.kokenCore && !handlers?.JSBridge;
};

/**
 * Vérifie si l'application s'exécute dans Median ou une Webview native
 */
export const isMedianNative = (): boolean => {
  if (isMobileApp()) return true;
  try {
    return Median.isNativeApp();
  } catch {
    // Fallback vers window.median
    return typeof window !== 'undefined' && window.median?.isNative === true;
  }
};

/**
 * Vérifie si l'application s'exécute dans un environnement mobile natif (Median)
 */
export const isNativeMobile = (): boolean => {
  return isMedianNative();
};

/**
 * Obtient la plateforme actuelle
 */
export const getPlatform = (): 'ios' | 'android' | 'web' => {
  if (isAndroidApp()) return 'android';
  if (isMobileApp()) return 'ios'; // Si c'est mobile mais pas Android, c'est iOS
  if (isMedianNative()) {
    if (typeof window !== 'undefined' && window.median?.platform) {
      return window.median.platform;
    }
  }
  return 'web';
};

// ============== TEMPORARY FEATURE FLAGS ==============
// Set to true to disable features until fully tested
const BIOMETRIC_TEMPORARILY_DISABLED = false;

/**
 * Vérifie si un Health Bridge est disponible.
 *
 * Stratégie Koken = 100 % custom (pas de plan payant Median souscrit) :
 *   1. iOS : plugin natif `KokenHealthKit.swift` → `window.kokenHealth` ✅
 *   2. Android : module Kotlin Santé Connect (coque `koken-ios-build`, dossier
 *      android/), qui expose `window.kokenHealth` via son shim JS, canal natif
 *      `window.kokenHealthAndroid` ; pas encore publié sur le Play Store ni vu en
 *      synchronisation réelle.
 *
 * Validé end-to-end le 2026-04-10 sur iPhone 13 Pro physique avec
 * KokenHealthKit (5903 pas retournés). Voir KokenHealthKit.swift et
 * KokenHealthKitBridge.js dans le repo iOS `koken-ios-build`.
 *
 * Le check `window.median?.healthBridge` ci-dessous est un safety net
 * dormant : ce plugin payant Median n'est pas souscrit chez Koken, donc
 * la condition retourne toujours `false` en prod.
 */
export const isHealthBridgeAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;

  // 1. Custom KokenHealthKit bridge (iOS, natif Koken)
  if (
    !!(window as any).kokenHealth &&
    typeof (window as any).kokenHealth.getStepsToday === 'function'
  ) {
    return true;
  }

  // 2. Custom Koken Health Connect Android (via app Android)
  if ((window as any).kokenHealthAndroid) {
    return true;
  }

  // 3. Safety net dormant Median HealthBridge (non souscrit → toujours false en prod)
  return isMedianNative() && typeof (window as any).median?.healthBridge !== 'undefined';
};

/**
 * Vérifie si OneSignal (notifications) est disponible via Median
 */
export const isMedianOneSignalAvailable = (): boolean => {
  return isMedianNative() &&
    typeof window !== 'undefined' &&
    typeof window.median?.onesignal !== 'undefined';
};

/**
 * Vérifie si les haptics Median sont disponibles
 */
export const isMedianHapticsAvailable = (): boolean => {
  return isMedianNative() &&
    typeof window !== 'undefined' &&
    typeof window.median?.haptics !== 'undefined';
};

/**
 * Vérifie si la biométrie Median est disponible
 * TEMPORARILY DISABLED - returns false until feature is ready
 */
export const isMedianBiometricAvailable = (): boolean => {
  if (BIOMETRIC_TEMPORARILY_DISABLED) return false;
  return isMedianNative() &&
    typeof window !== 'undefined' &&
    typeof window.median?.auth !== 'undefined';
};

/**
 * Obtient le type d'environnement détaillé
 */
export type EnvironmentType = 'web' | 'median-native';

export const getEnvironmentType = (): EnvironmentType => {
  if (isMedianNative()) {
    return 'median-native';
  }
  return 'web';
};

/**
 * Résumé des capacités natives disponibles
 */
export interface NativeCapabilitiesSummary {
  environment: EnvironmentType;
  platform: 'ios' | 'android' | 'web';
  hasHaptics: boolean;
  hasPush: boolean;
  hasBiometric: boolean;
  hasHealth: boolean;
}

export const getNativeCapabilities = (): NativeCapabilitiesSummary => {
  const environment = getEnvironmentType();
  const platform = getPlatform();

  return {
    environment,
    platform,
    hasHaptics: isMedianHapticsAvailable(),
    hasPush: isMedianOneSignalAvailable(),
    hasBiometric: isMedianBiometricAvailable(),
    hasHealth: isHealthBridgeAvailable(),
  };
};
